import {firstValueFrom} from 'rxjs';
import {JobsSpecificationCaching} from '../caching/jobs-specification.caching';
import {
    CpmAEAllPluginSpecApiSchema,
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {TreeNode} from 'primeng/api';
import {JSONSchema7} from 'json-schema';
import {ECpmAEPluginFunctionTypeApiEnum} from '../../../gen/cpm-ae-api-ts/src/enums/ae-global/cpmae-plugins.api-enums';
import {TreeUtils} from '../../../sdk/src/utils/tree.utils';

export abstract class ActionJobsUtils {
    public static readonly ICON_PLUGIN = 'pi pi-cloud';
    public static readonly ICON_PLUGIN_FUNCTION = 'pi pi-microchip';
    public static readonly ICON_SCHEMA = 'pi pi-code';

    public static readonly OOJI_META_ORIGIN_PLUGIN = 'originPlugin';
    public static readonly OOJI_META_OBJECT_STORE_NOT_USER_SELECTABLE = 'objectStoreNotUserSelectable';

    // ===============================================================
    // ===============================================================

    /**
     * Build a hierarchical tree of all `input` / `output` schemas
     * defined by every action‑plugin in the active specification.
     *
     * The tree follows the shape expected by PrimeNG's `TreeNode`:
     * - **Root** – an invisible container (`{children: []}`).
     * - **Plugin** – one node per plugin (`icon: 'pi pi-cloud'`).
     * - **Function** – one node per function under its plugin
     *   (`icon: 'pi pi-microchip'`).
     * - **Type** – leaf nodes for each defined `input` or `output`
     *   schema (`icon: 'pi pi-code'`, `selectable: true`).
     *
     * The method awaits the active specification, validates its presence,
     * then iterates over every plugin and function to discover any
     * non‑empty schema properties.  For each such pair a leaf node is
     * added.  The final tree is returned as a single `TreeNode` that
     * can be passed directly to a `<p-tree>` component.
     *
     * @returns A promise that resolves to a fully populated `TreeNode`
     *          tree ready for UI consumption.
     *
     * @throws If the active specification for `'action'` cannot be found.
     *
     * @example
     * ```ts
     * ActionJobsUtil.getTypesTree()
     *   .then(tree => console.log(JSON.stringify(tree, null, 2)));
     * ```
     */
    public static async getTypesTree(): Promise<TreeNode<string>> {
        // Retrieve the action‑job specification from the cache.
        const specification = await firstValueFrom(
            JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
        );

        if (!specification) {
            throw new Error("No active specification found for jobsType 'Action'");
        }

        // The specification is known to be a CpmAEAllPluginSpecApiSchema.
        const allPluginSpec = specification as unknown as CpmAEAllPluginSpecApiSchema;

        // Collect every plugin/function pair that declares a non‑empty schema.
        const remaining: {plugin: string; types: string[]}[] = [];

        Object.keys(allPluginSpec.type_definitions).forEach((typeDefinitionKey) => {
            if (typeDefinitionKey.startsWith('OOJI')) return;

            const jsonSchema = allPluginSpec.type_definitions[typeDefinitionKey] as JSONSchema7;
            let pluginName = '<unk>';

            if ('_meta' in jsonSchema) {
                const meta = jsonSchema['_meta'] as Record<string, unknown>;
                if (ActionJobsUtils.OOJI_META_ORIGIN_PLUGIN in meta)
                    pluginName = meta[ActionJobsUtils.OOJI_META_ORIGIN_PLUGIN] as string;

                // Skip non‑user‑selectable types.
                if (ActionJobsUtils.OOJI_META_OBJECT_STORE_NOT_USER_SELECTABLE in meta) return;
            }

            let pluginNode = remaining.find((x) => x.plugin == pluginName);
            if (!pluginNode) {
                pluginNode = {
                    plugin: pluginName,
                    types: []
                };
                remaining.push(pluginNode);
            }

            pluginNode.types.push(jsonSchema!.$id!.replaceAll('.schema.json', ''));
        });

        // Start building the root node (invisible container).
        const rootNode: TreeNode<string> = {children: []};

        // Iterate over the pending items until all have been consumed.
        while (remaining.length > 0) {
            for (let i = 0; i < remaining.length; ++i) {
                const current = remaining[i];

                // Find or create the plugin node.
                let pluginNode: TreeNode<string> | undefined = rootNode.children?.find(
                    (x) => x.data === current.plugin
                );
                if (!pluginNode) {
                    pluginNode = {
                        key: current.plugin,
                        label: current.plugin,
                        data: current.plugin,
                        icon: ActionJobsUtils.ICON_PLUGIN,
                        expanded: false,
                        selectable: false,
                        children: []
                    };
                    rootNode.children!.push(pluginNode);
                }

                // Create the leaf nodes
                current.types.forEach((currentType) => {
                    const typeNode: TreeNode<string> = {
                        key: currentType,
                        label: currentType,
                        data: currentType,
                        icon: ActionJobsUtils.ICON_SCHEMA,
                        selectable: true,
                        parent: pluginNode
                    };
                    pluginNode.children!.push(typeNode);
                });

                // Remove the processed item and adjust the loop counter.
                remaining.splice(i, 1);
                i--;
            }
        }

        return rootNode;
    }

    public static async getFunctionsTree(
        functionType: ECpmAEPluginFunctionTypeApiEnum
    ): Promise<TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[]> {
        // Retrieve the action‑job specification from the cache.
        const specificationAppDetails = await firstValueFrom(
            JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
        );

        if (!specificationAppDetails) {
            throw new Error("No active specification found for jobsType 'Action'");
        }

        // The specification is known to be a CpmAEAllPluginSpecApiSchema.
        const allPluginSpec = specificationAppDetails as unknown as CpmAEAllPluginSpecApiSchema;

        const result: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[] = [];
        allPluginSpec.plugins.forEach((plugin) => {
            const pluginNode: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema> = {
                key: plugin.id,
                label: plugin.name,
                data: plugin,
                icon: ActionJobsUtils.ICON_PLUGIN,
                expanded: false,
                selectable: false,
                children: []
            };

            plugin.functions.forEach((pluginFunction) => {
                if (pluginFunction.function_type != functionType) return;

                pluginNode.children?.push({
                    key: `${pluginNode.key}.${pluginFunction.id}`,
                    label: `${pluginFunction.name}`,
                    data: pluginFunction,
                    icon: ActionJobsUtils.ICON_PLUGIN_FUNCTION,
                    expanded: false,
                    selectable: true,
                    parent: pluginNode,
                    children: []
                });
            });

            result.push(pluginNode);
        });

        TreeUtils.pruneTree(result, (node) => !node.parent && (!node.children || node.children.length === 0));
        TreeUtils.sortTree(result, (a, b) => a.label!.localeCompare(b.label!));

        return result;
    }
    public static functionsTreeKey(pluginId: string, pluginFunctionId: string) {
        return `${pluginId}.${pluginFunctionId}`;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Retrieve the JSON‑Schema definition for a given `input` or `output`
     * type of an action‑plugin.
     *
     * The schema key must follow the convention returned by
     * {@link ActionJobsUtils.getTypesTree} – a string composed of two parts
     * separated by a single `@`:
     *
     * ```text
     *   <type>@<pluginId>.<functionId>
     * ```
     *
     * where `type` is either `"input"` or `"output"`.
     *
     * The method performs the following steps:
     *
     * 1. **Fetch the active action‑job specification** via
     *    {@link JobsSpecificationCaching.getExecutorActiveSpecification}.
     * 2. **Validate the key** – it must contain exactly two parts
     *    (`type` and the `plugin.function` combination).  If the format is
     *    wrong an {@link Error} is thrown.
     * 3. **Locate the plugin** – the `pluginId` is extracted from the part
     *    after the `@` and split by `.` into `[pluginId, functionId]`.
     *    The plugin list is searched for a matching `id`.  If not found,
     *    an {@link Error} is thrown.
     * 4. **Locate the function** – within the found plugin the `functionId`
     *    is used to find the corresponding function.  If not found, an
     *    {@link Error} is thrown.
     * 5. **Return the requested schema** – based on the `type` part:
     *    * `"input"` → the function's `input_schema`.
     *    * `"output"` → the function's `output_schema`.
     *    If the type is neither, `undefined` is returned (future extension
     *    point for side‑type handling).
     *
     * @param schemaKey A string of the form `"input@pluginId.functionId"` or
     *                  `"output@pluginId.functionId"`.
     * @returns A promise that resolves to the matching
     *          {@link JSONSchema7} or `undefined` if the type is unsupported.
     * @throws {@link Error} if the specification cannot be retrieved, the
     *          key is malformed, or the plugin/function cannot be found.
     *
     * @example
     * ```ts
     * // Resolve the input schema for the 'audioEncode' function of
     * // plugin 'mediaTools'.
     * const schema = await ActionJobsUtil.getSchema('input@mediaTools.audioEncode');
     * ```
     */
    public static async getSchema(schemaKey: string): Promise<JSONSchema7 | undefined> {
        // Retrieve the action‑job specification from the cache.
        const specification = await firstValueFrom(
            JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
        );
        if (!specification) {
            throw new Error("No active specification found for jobsType 'Action'");
        }

        // The specification is known to be a CpmAEAllPluginSpecApiSchema.
        const allPluginSpec = specification as unknown as CpmAEAllPluginSpecApiSchema;
        const lookupKey = `${schemaKey}.schema.json`;

        const typeKeys = Object.keys(allPluginSpec.type_definitions);
        if (!typeKeys.includes(lookupKey))
            throw new Error(`No type with id ${schemaKey} found in active specification!`);

        return allPluginSpec.type_definitions[lookupKey] as JSONSchema7;
    }
}
